import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { MessageSquare, Send, Star, Quote, Loader2, User, CheckCircle, ExternalLink } from 'lucide-react';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { Input } from '../ui/input';
import { GoogleReviewButton } from '../reviews/GoogleReviewButton';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

// Generate a unique visitor ID
const getVisitorId = () => {
  let visitorId = localStorage.getItem('pharmacy_visitor_id');
  if (!visitorId) {
    visitorId = 'v_' + Math.random().toString(36).substr(2, 9);
    localStorage.setItem('pharmacy_visitor_id', visitorId);
  }
  return visitorId;
};

const TestimonialsSection = ({ language = 'fr' }) => {
  const [testimonials, setTestimonials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [authorName, setAuthorName] = useState('');
  const [content, setContent] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const visitorId = getVisitorId();

  useEffect(() => {
    fetchTestimonials();
  }, []);

  const fetchTestimonials = async () => {
    try {
      const response = await axios.get(`${API_URL}/community/testimonials`);
      setTestimonials(response.data || []);
    } catch (error) {
      console.error('Error fetching testimonials:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!content.trim()) {
      toast.error('Veuillez écrire votre message');
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/community/testimonials`, {
        author_name: authorName || 'Anonyme',
        content: content,
        visitor_id: visitorId
      });
      setSubmitted(true);
      setContent('');
      setAuthorName('');
      toast.success('Merci pour votre avis !');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'envoi');
    } finally {
      setSubmitting(false);
    }
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  return (
    <section className="py-12 bg-white">
      <div className="max-w-6xl mx-auto px-4">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-10"
        >
          <div className="inline-flex items-center gap-2 bg-blue-50 text-blue-600 px-4 py-1.5 rounded-full text-sm font-medium mb-4">
            <MessageSquare className="w-4 h-4" />
            {language === 'fr' ? 'Vos Avis' : 'Your Reviews'}
          </div>
          <h2 className="font-display text-2xl md:text-3xl font-bold text-gray-900 mb-2">
            {language === 'fr' ? 'Ce que disent nos clients' : 'What our clients say'}
          </h2>
          <p className="text-gray-600 text-sm max-w-xl mx-auto">
            {language === 'fr' 
              ? 'Partagez votre expérience avec la Pharmacie Saint Ange Des Amis'
              : 'Share your experience with Pharmacie Saint Ange Des Amis'}
          </p>
        </motion.div>

        {/* Leave a Review Button */}
        {!showForm && !submitted && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-10"
          >
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Button
                onClick={() => setShowForm(true)}
                className="bg-[#2E7D32] hover:bg-[#1B5E20] text-white px-6 py-3 rounded-xl shadow-lg hover:shadow-xl transition-all"
              >
                <MessageSquare className="w-5 h-5 mr-2" />
                {language === 'fr' ? 'Laisser un avis ici' : 'Leave a review here'}
              </Button>
              <span className="text-gray-400 text-sm">ou</span>
              <GoogleReviewButton variant="default" showQROption={true} />
            </div>
          </motion.div>
        )}

        {/* Submission Success */}
        {submitted && (
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="max-w-lg mx-auto mb-10 bg-green-50 border border-green-200 rounded-xl p-6 text-center"
          >
            <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-3" />
            <h3 className="font-semibold text-green-800 mb-2">Merci pour votre avis !</h3>
            <p className="text-green-600 text-sm">
              Votre message sera publié après validation par notre équipe.
            </p>
            <Button
              variant="outline"
              onClick={() => { setSubmitted(false); setShowForm(false); }}
              className="mt-4"
            >
              Fermer
            </Button>
          </motion.div>
        )}

        {/* Review Form */}
        {showForm && !submitted && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-lg mx-auto mb-10"
          >
            <form
              onSubmit={handleSubmit}
              className="bg-gray-50 rounded-xl p-6 border border-gray-200"
            >
              <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <Quote className="w-5 h-5 text-[#2E7D32]" />
                {language === 'fr' ? 'Partagez votre expérience' : 'Share your experience'}
              </h3>
              
              <div className="space-y-4">
                <div>
                  <label className="text-sm font-medium text-gray-700 block mb-1">
                    {language === 'fr' ? 'Votre prénom (optionnel)' : 'Your first name (optional)'}
                  </label>
                  <Input
                    placeholder="Ex: Marie"
                    value={authorName}
                    onChange={(e) => setAuthorName(e.target.value)}
                    maxLength={50}
                  />
                </div>
                
                <div>
                  <label className="text-sm font-medium text-gray-700 block mb-1">
                    {language === 'fr' ? 'Votre message *' : 'Your message *'}
                  </label>
                  <Textarea
                    placeholder={language === 'fr' 
                      ? "Partagez votre expérience avec notre pharmacie..."
                      : "Share your experience with our pharmacy..."}
                    value={content}
                    onChange={(e) => setContent(e.target.value)}
                    rows={4}
                    maxLength={1000}
                    required
                  />
                  <p className="text-xs text-gray-500 mt-1 text-right">
                    {content.length}/1000
                  </p>
                </div>

                <div className="flex justify-end gap-2">
                  <Button
                    type="button"
                    variant="ghost"
                    onClick={() => setShowForm(false)}
                  >
                    {language === 'fr' ? 'Annuler' : 'Cancel'}
                  </Button>
                  <Button
                    type="submit"
                    disabled={submitting || !content.trim()}
                    className="bg-[#2E7D32] hover:bg-[#1B5E20]"
                  >
                    {submitting ? (
                      <Loader2 className="w-4 h-4 animate-spin mr-2" />
                    ) : (
                      <Send className="w-4 h-4 mr-2" />
                    )}
                    {language === 'fr' ? 'Envoyer' : 'Submit'}
                  </Button>
                </div>
              </div>
            </form>
          </motion.div>
        )}

        {/* Testimonials Grid */}
        {loading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-[#2E7D32]" />
          </div>
        ) : testimonials.length === 0 ? (
          <div className="text-center py-12 text-gray-500">
            <MessageSquare className="w-12 h-12 mx-auto text-gray-300 mb-3" />
            <p>{language === 'fr' ? 'Aucun avis pour le moment' : 'No reviews yet'}</p>
            <p className="text-sm">
              {language === 'fr' ? 'Soyez le premier à partager votre expérience !' : 'Be the first to share your experience!'}
            </p>
          </div>
        ) : (
          <motion.div
            className="grid md:grid-cols-2 lg:grid-cols-3 gap-6"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.1 }}
            variants={{
              hidden: { opacity: 0 },
              visible: { opacity: 1, transition: { staggerChildren: 0.1 } }
            }}
          >
            {testimonials.map((testimonial) => (
              <motion.div
                key={testimonial.id}
                variants={{
                  hidden: { opacity: 0, y: 20 },
                  visible: { opacity: 1, y: 0 }
                }}
                className={`relative bg-white rounded-xl p-6 shadow-sm hover:shadow-md transition-shadow border ${
                  testimonial.is_featured 
                    ? 'border-amber-200 ring-2 ring-amber-100' 
                    : 'border-gray-100'
                }`}
              >
                {/* Featured badge */}
                {testimonial.is_featured && (
                  <div className="absolute -top-3 -right-3">
                    <div className="bg-amber-500 text-white p-2 rounded-full shadow-lg">
                      <Star className="w-4 h-4 fill-white" />
                    </div>
                  </div>
                )}

                {/* Quote icon */}
                <Quote className="w-8 h-8 text-[#2E7D32]/20 mb-3" />

                {/* Content */}
                <p className="text-gray-600 leading-relaxed mb-4">
                  "{testimonial.content}"
                </p>

                {/* Author */}
                <div className="flex items-center gap-3 pt-4 border-t border-gray-100">
                  <div className="w-10 h-10 bg-[#E8F5E9] rounded-full flex items-center justify-center">
                    <span className="text-lg font-bold text-[#2E7D32]">
                      {testimonial.author_name.charAt(0).toUpperCase()}
                    </span>
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">{testimonial.author_name}</p>
                    <p className="text-xs text-gray-500">
                      {formatDate(testimonial.created_at)}
                    </p>
                  </div>
                </div>
              </motion.div>
            ))}
          </motion.div>
        )}
      </div>
    </section>
  );
};

export default TestimonialsSection;
